import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ShoppingCart,
  Menu,
  UserCircle,
  LogOut,
  ChevronDown,
} from "lucide-react";
import { Modal } from "flowbite-react";
import logo from "../../assets/images/logo.png";
import { cartContext } from "../../Context/CartContext";
import Marquee from "react-fast-marquee";

const NavbarComponent = ({ userData, logOut }) => {
  const { numOfCartItems, isCartLoaded } = useContext(cartContext);
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset;
      setIsScrolled(position > 80);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    const handleClickOutside = (event) => {
      if (!event.target.closest(".profile-menu")) {
        setIsProfileMenuOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    window.addEventListener("click", handleClickOutside);
    handleResize();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setOpenModal(false);
    setIsProfileMenuOpen(false);
    logOut();
  };

  const isAdmin = userData?.role === "admin";

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/Products", label: "Products" },
    { path: "/Categories", label: "Categories" },
    { path: "/myorders", label: "My Orders" },
    { path: "/about", label: "About" },
  ];

  const adminLinks = [
    { path: "/Items", label: "Admin Dashboard" },
    { path: "/orders", label: "Orders Dashboard" },
  ];

  const isActiveLink = (path) => location.pathname === path;

  const UserProfileMenu = () => (
    <div className="relative profile-menu">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsProfileMenuOpen(!isProfileMenuOpen);
        }}
        className="flex items-center space-x-2 group"
      >
        <UserCircle className="w-6 h-6 text-gray-700 group-hover:text-gray-900" />
        <span className="hidden md:block text-sm font-medium text-gray-700 group-hover:text-gray-900">
          {userData?.name || "User"}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            isProfileMenuOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isProfileMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">
              {userData?.name}
            </p>
            <p className="text-xs text-gray-500">{userData?.email}</p>
          </div>
          <button
            onClick={() => setOpenModal(true)}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="sticky top-0 z-50 w-full">
      {/* Upper Ticker */}
      <div className="bg-nav from-gray-800 to-black text-white w-full">
        <Marquee speed={50} gradient={false}>
          <div
            dir="rtl"
            className="py-1 font-medium text-white text-sm tracking-wider"
          >
            {/* plain text, no <span> — a global `span` rule in index.css paints white backgrounds */}
            {"✨ يتم تأكيد ال order على رقم WhatsApp بدفع مبلغ الشحن ✨   |   ".repeat(
              6
            )}
          </div>
        </Marquee>
      </div>

      {/* Main Nav Content */}
      <nav
        className={`
          w-full px-4 transition-all duration-300 
          ${isScrolled ? "bg-white shadow-md" : "bg-transparent"}
        `}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Mobile Menu Toggle */}
          {isMobile && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-800 hover:text-gray-600 transition-colors"
            >
              <Menu size={28} />
            </button>
          )}

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={logo}
              alt="Logo"
              className="w-32 ml-5 hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Navigation */}
          {!isMobile && (
            <div className="flex space-x-6 items-center">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    text-gray-800 font-medium 
                    hover:text-black transition-colors
                    ${
                      isActiveLink(link.path)
                        ? "text-black border-b-2 border-black"
                        : "hover:border-b hover:border-gray-300"
                    }
                  `}
                >
                  {link.label}
                </Link>
              ))}

              {/* Admin Links */}
              {isAdmin &&
                adminLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`
                    text-gray-800 font-medium 
                    hover:text-green-600 transition-colors
                    ${
                      isActiveLink(link.path)
                        ? "text-green-600 border-b-2 border-green-600"
                        : "hover:border-b hover:border-gray-300"
                    }
                  `}
                  >
                    {link.label}
                  </Link>
                ))}
            </div>
          )}

          {/* Icons and Auth */}
          <div className="flex items-center space-x-4">
            <Link to="/Cart" className="relative group">
              <ShoppingCart
                size={24}
                className="text-gray-800 hover:text-black transition-colors"
              />
              {numOfCartItems > 0 && (
                <span
                  className="absolute -top-2.5 -right-2.5 
                  bg-red-500 text-white rounded-full 
                  w-5 h-5 flex items-center justify-center 
                  text-xs font-bold animate-pulse"
                >
                  {numOfCartItems}
                </span>
              )}
            </Link>

            {userData ? (
              <UserProfileMenu />
            ) : (
              <div className="flex space-x-4">
                <Link
                  to="/Login"
                  className="
                    text-gray-800 hover:text-black 
                    transition-colors font-medium
                    border-b border-transparent hover:border-black"
                >
                  Login
                </Link>
                <Link
                  to="/Register"
                  className="
                    text-white bg-black px-3 py-1 
                    rounded-full hover:bg-gray-900 
                    transition-colors font-medium"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobile && isOpen && (
          <div
            className="
            bg-white shadow-lg absolute 
            top-full left-0 w-full 
            py-4 space-y-2"
          >
            {[...navLinks, ...(isAdmin ? adminLinks : [])].map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  block px-4 py-2 
                  ${
                    isActiveLink(link.path)
                      ? "bg-gray-100 text-black"
                      : "hover:bg-gray-100"
                  }
                `}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            {userData ? (
              <button
                onClick={() => {
                  setIsOpen(false);
                  setOpenModal(true);
                }}
                className="
                  w-full text-left px-4 py-2 
                  hover:bg-red-50 text-gray-800 
                  hover:text-red-600"
              >
                Logout
              </button>
            ) : (
              <>
                <Link
                  to="/Login"
                  className="block px-4 py-2 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/Register"
                  className="block px-4 py-2 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  Register
                </Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Logout Modal */}
      <Modal
        show={openModal}
        size="md"
        onClose={() => setOpenModal(false)}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <div className="mx-auto mb-4 text-gray-500">
              <LogOut className="mx-auto h-14 w-14" />
            </div>
            <h3 className="mb-5 text-lg font-normal text-gray-600">
              Are you sure you want to logout?
            </h3>
            <div className="flex justify-center gap-4">
              <button
                onClick={handleLogout}
                className="
                  bg-red-600 text-white 
                  px-4 py-2 rounded-md 
                  hover:bg-red-700 transition-colors"
              >
                Yes, Logout
              </button>
              <button
                onClick={() => setOpenModal(false)}
                className="
                  bg-gray-200 text-gray-800 
                  px-4 py-2 rounded-md 
                  hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NavbarComponent;
